import {
  replaceIdMiddleware,
  titleMiddleware,
} from '@blocksuite/affine-shared/adapters';
import { sha } from '@blocksuite/global/utils';
import type { DocSnapshot, Schema, Store, Workspace } from '@blocksuite/store';
import {
  ensureWorkspaceDoc,
  extMimeMap,
  getAssetName,
  Transformer,
} from '@blocksuite/store';
import {
  addBlobId,
  assertPagePartsLoaded,
  blockHasDatabaseShardDirectory,
  classifyPageSubdocId,
  collectDatabaseCellBlobIds,
  createServerDatabaseRootDocId,
  createServerDatabaseRowShardDocId,
  createServerPagePartDocId,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseBlobCellKeys,
  databaseBlockBlobCellKeys,
  databaseCellKeysOfType,
  databaseShardDirectoryPartMap,
  type DatabaseShardManifest,
  deleteDatabaseShardDirectory,
  FILE_INDEX_MANAGED_BY,
  isFileIndexDatabaseId,
  isHiddenSystemManagedDatabase,
  isPageDocumentId,
  PAGE_DOCUMENT_PART_ID,
  pagePartBlocksMap,
  pagePartElementsMap,
  pagePlacedBlockIds,
  pageReadSource,
  pageStructureMap,
  purgePageSubtrees,
  readDatabaseShardDirectory,
  readDatabaseShardDirectoryBlock,
  readDatabaseShardDirectoryBlockPartDocIds,
  readDatabaseShardDirectoryDocIds,
  readPage,
  readPagePartDirectory,
  readPageStructureEntries,
  readPageSubdocId,
  remapPageStructureIds,
  upgradePageDocument,
  writeNewDatabaseShardDirectory,
} from '@streamlain/database-identity';
import { upgradeLegacyDocSnapshot } from '@streamlain/ydoc-archive-upgrader';
import * as Y from 'yjs';

import {
  createStreamlainIdTracker,
  remapStreamlainReferences,
} from './streamlain-docid-middleware.js';
import { download, StreamingZip, Unzip, Zip } from './utils.js';

type YDocArchiveEntry = {
  readonly sourceDocId: string;
  readonly ydoc: Y.Doc;
};

type YDocArchiveDatabaseSystemDocAccessBinding = {
  readonly databaseId: string;
  readonly rootDocId: string;
  readonly accessDocId: string;
  readonly blockId: string;
  readonly manifest: DatabaseShardManifest;
};

type YDocArchiveImportTarget = {
  readonly sourceDocId: string;
  readonly targetDocId: string;
  readonly system: boolean;
  readonly blockIdReplacements: readonly {
    readonly sourceBlockId: string;
    readonly targetBlockId: string;
  }[];
  readonly databaseSystemDocAccess: readonly YDocArchiveDatabaseSystemDocAccessBinding[];
};

type ZipImportProgress = {
  readonly phase:
    | 'snapshot-reading'
    | 'snapshot-preparing'
    | 'snapshot-remapping'
    | 'snapshot-checking'
    | 'snapshot-assets'
    | 'snapshot-importing';
  readonly processed: number;
  readonly total: number;
  readonly phases?: readonly ZipImportProgress['phase'][];
};

type ZipImportOptions = {
  readonly onProgress?: (progress: ZipImportProgress) => void;
  /**
   * Called after archive doc IDs are remapped, but before target docs are
   * loaded and attached to live sync.
   */
  readonly beforeYDocArchiveDocsLoaded?: (
    targets: readonly YDocArchiveImportTarget[]
  ) => Promise<void>;
  /**
   * Publishes the remapped database root and row shard docs through the
   * workspace publication journal. Archives with database docs require it:
   * a direct write into those docs is never sent to the server. `pages` names,
   * by database root, the page documents of the archive that hold its block.
   */
  readonly publishDatabaseArchiveDocs?: (
    documents: readonly { docId: string; update: Uint8Array }[],
    pages: ReadonlyMap<string, readonly { docId: string; update: Uint8Array }[]>
  ) => Promise<unknown>;
  /**
   * Load non-system page docs without adding them to document metadata.
   * Marketplace append imports use this for temporary source docs that are
   * copied into an already existing target doc and discarded immediately after.
   */
  readonly transientPageDocs?: boolean;
  readonly onYDocArchiveProgress?: (progress: {
    readonly processedDocs: number;
    readonly totalDocs: number;
  }) => void;
};

type BlobColumnLookup = ReadonlyMap<string, DatabaseBlobCellKeys>;

const DATABASE_INLINE_CELLS_PROP = 'prop:cells';
const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

export function writeArchiveDocumentTitle(
  ydoc: Y.Doc,
  title: string | undefined
): void {
  if (typeof title === 'string' && title.trim()) {
    ydoc.getMap('meta').set('title', title);
  }
}

export function restoreArchiveDocumentTitle(
  meta: { setDocMeta(id: string, props: { title: string }): void },
  docId: string,
  ydoc: Y.Doc
): void {
  const title = ydoc.getMap('meta').get('title');
  if (typeof title === 'string' && title.trim()) {
    meta.setDocMeta(docId, { title });
  }
}

function readArchiveDocId(path: string): string | null {
  const match = /^docs\/(.+)\.ydoc$/.exec(path);
  return match?.[1] ?? null;
}

export function removeSourceWorkspacePlacement(ydoc: Y.Doc): void {
  ydoc.getMap('meta').delete(INITIAL_TARGET_FOLDER_ID_META_KEY);
}

function isArchiveDirectory(path: string): boolean {
  return path.endsWith('/');
}

function isSystemArchiveDocId(docId: string): boolean {
  return docId.startsWith('db$');
}

function isPagePartDocId(docId: string): boolean {
  return readPageSubdocId(docId)?.subdoc.kind === 'page_part';
}

/**
 * The archive entries of a page: its document, then its part documents, each
 * of which must be in memory, so a page is never exported in half. Hidden
 * File Index databases are purged across all of them.
 */
function pageArchiveEntries(
  collection: Workspace,
  store: Store
): YDocArchiveEntry[] {
  const head = cloneStoreYDocForArchive(store);
  const parts = new Map<string, YDocArchiveEntry>();
  try {
    if (!isSystemArchiveDocId(store.id))
      readPagePartArchiveEntries(collection, store, parts);
    pruneImportedFileIndexSystemDatabases(
      head,
      new Map([...parts].map(([partId, entry]) => [partId, entry.ydoc]))
    );
  } catch (error) {
    head.destroy();
    for (const entry of parts.values()) entry.ydoc.destroy();
    throw error;
  }
  return [{ sourceDocId: store.id, ydoc: head }, ...parts.values()];
}

function readPagePartArchiveEntries(
  collection: Workspace,
  store: Store,
  entries: Map<string, YDocArchiveEntry>
): void {
  for (const partId of readPagePartDirectory(store.spaceDoc).keys()) {
    if (partId === PAGE_DOCUMENT_PART_ID) continue;
    const docId = createServerPagePartDocId({
      workspaceId: collection.id,
      pageId: store.id,
      partId,
    });
    const doc = collection.getDoc(docId);
    if (!doc || !store.pageStructure.docs.loadedPartIds().includes(partId)) {
      throw new Error(
        `Cannot export page "${store.meta?.title || store.id}" because its part "${partId}" is not loaded.`
      );
    }
    const ydoc = new Y.Doc({ guid: docId });
    Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(doc.spaceDoc));
    entries.set(partId, { sourceDocId: docId, ydoc });
  }
}

function registerArchiveDoc(collection: Workspace, docId: string) {
  const registerDoc = (collection as { registerDoc?: (id: string) => void })
    .registerDoc;
  if (registerDoc) {
    registerDoc.call(collection, docId);
    const registeredDoc = collection.getDoc(docId);
    if (!registeredDoc) {
      throw new Error(`Failed to register system archive doc "${docId}".`);
    }
    return registeredDoc;
  }

  return null;
}

function createArchiveDoc(
  collection: Workspace,
  docId: string,
  options: { transientPageDoc?: boolean } = {}
) {
  if (options.transientPageDoc) {
    const transientDoc = registerArchiveDoc(collection, docId);
    if (!transientDoc) {
      throw new Error(
        `Cannot load transient archive doc "${docId}" without registerDoc support.`
      );
    }
    return transientDoc;
  }

  return collection.createDoc(docId);
}

function isYMap(value: unknown): value is Y.Map<unknown> {
  return value instanceof Y.Map;
}

function isYArray(value: unknown): value is Y.Array<unknown> {
  return value instanceof Y.Array;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Y.AbstractType)
  );
}

function cloneYValue(value: unknown): unknown {
  if (value instanceof Y.AbstractType) {
    return value.clone();
  }
  if (Array.isArray(value)) {
    return value.map(cloneYValue);
  }
  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }
  return value;
}

function cloneYMap(source: Y.Map<unknown>) {
  const target = new Y.Map<unknown>();
  source.forEach((value, key) => {
    target.set(key, cloneYValue(value));
  });
  return target;
}

function createTargetDatabaseRootDocId(
  collection: Workspace,
  databaseId: string
): string {
  return createServerDatabaseRootDocId({
    workspaceId: collection.id,
    databaseId,
  });
}

function remapStringField(
  block: Y.Map<unknown>,
  key: string,
  map: ReadonlyMap<string, string>
) {
  const value = block.get(key);
  if (typeof value !== 'string') return;
  const mapped = map.get(value);
  if (mapped) block.set(key, mapped);
}

function remapAutomationTables(
  value: unknown,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>
) {
  if (!isYArray(value)) return;

  value.forEach(item => {
    if (!isYMap(item)) return;
    remapStringField(item, 'docId', docIdMap);
    remapStringField(item, 'tableId', blockIdMap);
    remapStringField(item, 'blockId', blockIdMap);
  });
}

function remapYMapArrayStringField(
  value: unknown,
  key: string,
  map: ReadonlyMap<string, string>
) {
  if (!isYArray(value)) return;

  value.forEach(item => {
    if (!isYMap(item)) return;
    remapStringField(item, key, map);
  });
}

function remapConnectionEndpoint(
  endpoint: unknown,
  blockIdMap: ReadonlyMap<string, string>
): unknown {
  if (isYMap(endpoint)) {
    remapStringField(endpoint, 'id', blockIdMap);
    return endpoint;
  }

  if (!isPlainRecord(endpoint) || typeof endpoint.id !== 'string') {
    return endpoint;
  }

  const mapped = blockIdMap.get(endpoint.id);
  return mapped ? { ...endpoint, id: mapped } : endpoint;
}

function remapMindmapChildValue(
  value: unknown,
  blockIdMap: ReadonlyMap<string, string>
): unknown {
  if (isYMap(value)) {
    remapStringField(value, 'parent', blockIdMap);
    return value;
  }

  if (!isPlainRecord(value) || typeof value.parent !== 'string') {
    return value;
  }

  const mappedParent = blockIdMap.get(value.parent);
  return mappedParent ? { ...value, parent: mappedParent } : value;
}

function remapSurfaceElementChildren(
  children: unknown,
  blockIdMap: ReadonlyMap<string, string>,
  remapParent: boolean
): unknown {
  if (isYMap(children)) {
    const entries = Array.from(children.entries());
    for (const [sourceId, value] of entries) {
      const targetId = blockIdMap.get(sourceId) ?? sourceId;
      const targetValue = remapParent
        ? remapMindmapChildValue(value, blockIdMap)
        : value;

      if (targetId === sourceId && targetValue === value) continue;

      children.delete(sourceId);
      children.set(targetId, targetValue);
    }
    return children;
  }

  if (!isPlainRecord(children)) {
    return children;
  }

  let changed = false;
  const remapped: Record<string, unknown> = {};
  for (const [sourceId, value] of Object.entries(children)) {
    const targetId = blockIdMap.get(sourceId) ?? sourceId;
    const targetValue = remapParent
      ? remapMindmapChildValue(value, blockIdMap)
      : value;
    remapped[targetId] = targetValue;
    changed ||= targetId !== sourceId || targetValue !== value;
  }

  return changed ? remapped : children;
}

function readElementProp(element: unknown, key: string): unknown {
  if (isYMap(element)) return element.get(key);
  if (isPlainRecord(element)) return element[key];
  return undefined;
}

function writeElementProp(element: unknown, key: string, value: unknown): void {
  if (isYMap(element)) {
    element.set(key, value);
  } else if (isPlainRecord(element)) {
    element[key] = value;
  }
}

function remapSurfaceElement(
  element: unknown,
  blockIdMap: ReadonlyMap<string, string>
): void {
  const type = readElementProp(element, 'type');

  if (type === 'connector') {
    const source = readElementProp(element, 'source');
    const target = readElementProp(element, 'target');
    const remappedSource = remapConnectionEndpoint(source, blockIdMap);
    const remappedTarget = remapConnectionEndpoint(target, blockIdMap);

    if (remappedSource !== source) {
      writeElementProp(element, 'source', remappedSource);
    }
    if (remappedTarget !== target) {
      writeElementProp(element, 'target', remappedTarget);
    }
    return;
  }

  if (type === 'group' || type === 'mindmap') {
    const children = readElementProp(element, 'children');
    const remappedChildren = remapSurfaceElementChildren(
      children,
      blockIdMap,
      type === 'mindmap'
    );
    if (remappedChildren !== children) {
      writeElementProp(element, 'children', remappedChildren);
    }
  }
}

function remapRelationColumns(
  columns: unknown,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>
) {
  if (!isYArray(columns)) return;

  columns.forEach(item => {
    if (!isYMap(item) || item.get('type') !== 'relation') return;

    const data = item.get('data');
    if (!isYMap(data)) return;

    remapStringField(data, 'targetDocId', docIdMap);
    remapStringField(data, 'targetDatabaseId', blockIdMap);
    remapStringField(data, 'targetBlockId', blockIdMap);
    remapStringField(data, 'blockId', blockIdMap);
  });
}

/**
 * Moves the directory of every archived database block to its target
 * database: the head on the block and fresh part documents for the target
 * workspace, which replace the archived part documents. Returns the entries
 * to import.
 */
function remapDatabaseDirectories(
  collection: Workspace,
  entries: readonly YDocArchiveEntry[],
  blockIdMap: ReadonlyMap<string, string>
): YDocArchiveEntry[] {
  const bySource = new Map(entries.map(entry => [entry.sourceDocId, entry]));
  const replaced = new Set<string>();
  const created: YDocArchiveEntry[] = [];
  for (const entry of entries) {
    for (const [blockId, block] of collectYDatabaseBlocks(entry.ydoc)) {
      if (!blockHasDatabaseShardDirectory(block)) continue;
      const databaseId = blockIdMap.get(blockId);
      if (!databaseId) {
        throw new Error(
          `Database block "${blockId}" has no import id mapping.`
        );
      }
      const sourcePartDocIds = readDatabaseShardDirectoryBlockPartDocIds(block);
      const source = readDatabaseShardDirectoryBlock(block, partDocId => {
        const part = bySource.get(partDocId);
        return part && databaseShardDirectoryPartMap(part.ydoc);
      });
      // The block stores a directory: a read without one is a broken archive.
      if (!source)
        throw new Error(
          `Database block "${blockId}" stores no readable directory.`
        );
      sourcePartDocIds.forEach(partDocId => replaced.add(partDocId));
      const parts = entry.ydoc.transact(() => {
        deleteDatabaseShardDirectory(block);
        return writeNewDatabaseShardDirectory(block, {
          ...source,
          databaseId,
          databaseRootDocId: createTargetDatabaseRootDocId(
            collection,
            databaseId
          ),
          workspaceId: collection.id,
          shards: source.shards.map(shard => ({
            ...shard,
            serverDocId: createServerDatabaseRowShardDocId({
              workspaceId: collection.id,
              databaseId,
              shardId: shard.shardId,
            }),
          })),
        });
      });
      for (const [sourceDocId, ydoc] of parts)
        created.push({ sourceDocId, ydoc });
    }
  }
  for (const partDocId of replaced) bySource.get(partDocId)?.ydoc.destroy();
  return [
    ...entries.filter(entry => !replaced.has(entry.sourceDocId)),
    ...created,
  ];
}

function readYString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function collectYDatabaseBlocks(ydoc: Y.Doc): [string, Y.Map<unknown>][] {
  const blocks = ydoc.getMap<unknown>('blocks');
  const databaseBlocks: [string, Y.Map<unknown>][] = [];
  blocks.forEach((block, blockId) => {
    if (!isYMap(block) || block.get('sys:flavour') !== 'affine:database') {
      return;
    }
    databaseBlocks.push([blockId, block]);
  });
  return databaseBlocks;
}

/**
 * The page documents of the archive that hold each database block, by its
 * root document: the page and, for a block in a part, that part. A new
 * database publishes with them, so the server binds it in that send.
 */
function collectArchiveDatabasePages(
  entries: readonly YDocArchiveEntry[],
  docIdMap: ReadonlyMap<string, string>
): ReadonlyMap<string, { docId: string; update: Uint8Array }[]> {
  const pages = new Map<string, Y.Doc>();
  for (const entry of entries) {
    const docId = docIdMap.get(entry.sourceDocId);
    if (docId && !isSystemArchiveDocId(docId)) pages.set(docId, entry.ydoc);
  }
  const hosts = new Map<string, { docId: string; update: Uint8Array }[]>();
  for (const entry of entries) {
    const docId = docIdMap.get(entry.sourceDocId);
    if (!docId) continue;
    const subdoc = readPageSubdocId(docId)?.subdoc;
    const pageId =
      subdoc?.kind === 'page_part'
        ? subdoc.pageId
        : isSystemArchiveDocId(docId)
          ? null
          : docId;
    const page = pageId === null ? undefined : pages.get(pageId);
    if (!pageId || !page) continue;
    for (const [blockId, block] of collectYDatabaseBlocks(entry.ydoc)) {
      const rootDocId = readYString(block.get('prop:databaseRootDocId'));
      if (!rootDocId) continue;
      if (hosts.has(rootDocId))
        throw new Error(
          `Archive database "${rootDocId}" has a second block "${blockId}".`
        );
      hosts.set(rootDocId, [
        { docId: pageId, update: Y.encodeStateAsUpdate(page) },
        ...(entry.ydoc === page
          ? []
          : [{ docId, update: Y.encodeStateAsUpdate(entry.ydoc) }]),
      ]);
    }
  }
  return hosts;
}

function collectPreLoadDatabaseSystemDocAccessBindings(
  entries: readonly YDocArchiveEntry[],
  docIdMap: ReadonlyMap<string, string>
): ReadonlyMap<string, YDocArchiveDatabaseSystemDocAccessBinding[]> {
  const rootsByDatabaseId = new Map<
    string,
    { rootDocId: string; manifest: DatabaseShardManifest }
  >();
  const byTarget = new Map(
    entries.map(entry => [docIdMap.get(entry.sourceDocId), entry.ydoc])
  );

  for (const entry of entries) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId || !isSystemArchiveDocId(targetDocId)) continue;

    for (const [, block] of collectYDatabaseBlocks(entry.ydoc)) {
      const databaseId = readYString(block.get('prop:databaseId'));
      const manifest = readDatabaseShardDirectoryBlock(block, partDocId => {
        const part = byTarget.get(partDocId);
        return part && databaseShardDirectoryPartMap(part);
      });
      if (!databaseId || !manifest) continue;

      rootsByDatabaseId.set(databaseId, {
        rootDocId:
          readYString(block.get('prop:databaseRootDocId')) ??
          manifest.databaseRootDocId,
        manifest,
      });
    }
  }

  const bindingsByAccessDocId = new Map<
    string,
    YDocArchiveDatabaseSystemDocAccessBinding[]
  >();

  for (const entry of entries) {
    const accessDocId = docIdMap.get(entry.sourceDocId);
    if (!accessDocId || isSystemArchiveDocId(accessDocId)) continue;

    for (const [blockId, block] of collectYDatabaseBlocks(entry.ydoc)) {
      const databaseId = readYString(block.get('prop:databaseId'));
      const rootDocId = readYString(block.get('prop:databaseRootDocId'));
      if (!databaseId || !rootDocId) continue;

      const root = rootsByDatabaseId.get(databaseId);
      if (!root || root.rootDocId !== rootDocId) continue;

      const bindings = bindingsByAccessDocId.get(accessDocId) ?? [];
      bindings.push({
        databaseId,
        rootDocId,
        accessDocId,
        blockId,
        manifest: root.manifest,
      });
      bindingsByAccessDocId.set(accessDocId, bindings);
    }
  }

  return bindingsByAccessDocId;
}

function collectBlockIds(
  collection: Workspace,
  entries: readonly YDocArchiveEntry[]
) {
  const blockIdMap = new Map<string, string>();

  for (const entry of entries) {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    blocks.forEach((_block, blockId) => {
      if (!blockIdMap.has(blockId)) {
        blockIdMap.set(blockId, collection.idGenerator());
      }
    });
  }

  return blockIdMap;
}

function buildDocIdMap(
  collection: Workspace,
  entries: readonly YDocArchiveEntry[],
  blockIdMap: ReadonlyMap<string, string>
) {
  const docIdMap = new Map<string, string>();

  for (const entry of entries) {
    // Archives can originate in a different workspace; remapping must read
    // the source identity rather than require the destination workspace.
    const root = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (root?.kind === 'database-root') {
      const databaseId = blockIdMap.get(root.databaseId);
      if (!databaseId) {
        throw new Error(
          `Database root doc "${entry.sourceDocId}" has no remapped database block "${root.databaseId}".`
        );
      }

      docIdMap.set(
        entry.sourceDocId,
        createTargetDatabaseRootDocId(collection, databaseId)
      );
      continue;
    }

    // Part documents of a directory were created for the target database.
    if (
      classifyPageSubdocId(entry.sourceDocId, collection.id)?.kind ===
      'database-directory-part'
    ) {
      docIdMap.set(entry.sourceDocId, entry.sourceDocId);
      continue;
    }

    const rowShard = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (rowShard?.kind === 'database-row-shard' && !rowShard.local) {
      const databaseId = blockIdMap.get(rowShard.databaseId);
      if (!databaseId) {
        throw new Error(
          `Database row shard doc "${entry.sourceDocId}" has no remapped database block "${rowShard.databaseId}".`
        );
      }

      docIdMap.set(
        entry.sourceDocId,
        createServerDatabaseRowShardDocId({
          workspaceId: collection.id,
          databaseId,
          shardId: rowShard.shardId,
        })
      );
      continue;
    }

    if (readPageSubdocId(entry.sourceDocId)?.subdoc.kind === 'page_part')
      continue;

    docIdMap.set(entry.sourceDocId, collection.idGenerator());
  }

  // The parts of a page follow the id of the page they were exported with.
  for (const entry of entries) {
    const part = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (part?.kind !== 'page_part') continue;
    const pageId = docIdMap.get(part.pageId);
    if (!pageId) {
      throw new Error(
        `Page part doc "${entry.sourceDocId}" has no page "${part.pageId}" in the archive.`
      );
    }
    docIdMap.set(
      entry.sourceDocId,
      createServerPagePartDocId({
        workspaceId: collection.id,
        pageId,
        partId: part.partId,
      })
    );
  }

  return docIdMap;
}

function remapYDocBlocks(
  collection: Workspace,
  ydoc: Y.Doc,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>,
  partId: string
) {
  const blocks = ydoc.getMap<unknown>('blocks');
  const entries = Array.from(blocks.entries());
  pagePartElementsMap(ydoc).forEach(element =>
    remapSurfaceElement(element, blockIdMap)
  );
  remapPageStructureIds(ydoc, blockIdMap, partId);

  for (const [sourceBlockId, block] of entries) {
    if (!isYMap(block)) continue;

    const targetBlockId = blockIdMap.get(sourceBlockId);
    if (!targetBlockId) {
      throw new Error(`Block "${sourceBlockId}" has no import id mapping.`);
    }

    const targetBlock =
      targetBlockId === sourceBlockId ? block : cloneYMap(block);
    if (targetBlockId !== sourceBlockId) {
      blocks.set(targetBlockId, targetBlock);
      blocks.delete(sourceBlockId);
    }

    targetBlock.set('sys:id', targetBlockId);

    const flavour = targetBlock.get('sys:flavour');
    if (flavour === 'affine:automation') {
      remapStringField(targetBlock, 'prop:sourceDocId', docIdMap);
      remapStringField(targetBlock, 'prop:sourceTableId', blockIdMap);
      remapAutomationTables(
        targetBlock.get('prop:inputTables'),
        docIdMap,
        blockIdMap
      );
      remapAutomationTables(
        targetBlock.get('prop:outputTables'),
        docIdMap,
        blockIdMap
      );
      remapYMapArrayStringField(
        targetBlock.get('prop:outputMappings'),
        'tableId',
        blockIdMap
      );
      remapYMapArrayStringField(
        targetBlock.get('prop:matchConfigs'),
        'targetTableId',
        blockIdMap
      );
    }

    if (flavour === 'affine:ghost') {
      remapStringField(targetBlock, 'prop:targetDocId', docIdMap);
    }

    if (flavour === 'affine:database') {
      const databaseId = targetBlockId;
      const databaseRootDocId = createTargetDatabaseRootDocId(
        collection,
        databaseId
      );
      targetBlock.set('prop:databaseId', databaseId);
      if (targetBlock.has('prop:databaseRootDocId')) {
        targetBlock.set('prop:databaseRootDocId', databaseRootDocId);
      }
      remapRelationColumns(
        targetBlock.get('prop:columns'),
        docIdMap,
        blockIdMap
      );
    }
  }
}

// Yield only for interactive imports; bulk/template consumers keep their API.
// A task boundary lets React commit the actual counts and the browser paint.
function importYield(options: ZipImportOptions): Promise<void> | undefined {
  if (!options.onProgress) return;
  return new Promise(resolve => setTimeout(resolve, 0));
}

function reportZipImport(
  options: ZipImportOptions,
  phase: ZipImportProgress['phase'],
  processed = 0,
  total = 0
) {
  options.onProgress?.({ phase, processed, total });
}

async function readYDocArchiveEntry(
  sourceDocId: string,
  blob: Blob
): Promise<YDocArchiveEntry> {
  let update: Uint8Array = new Uint8Array(await blob.arrayBuffer());
  if (isPageDocumentId(sourceDocId)) {
    const result = upgradePageDocument(update);
    if (result.status === 'refused') {
      throw new Error(`ZIP page ${sourceDocId}: ${result.reason}`);
    }
    update = result.bytes;
  }
  const ydoc = new Y.Doc({ guid: sourceDocId });
  try {
    Y.applyUpdate(ydoc, update);
    return { sourceDocId, ydoc };
  } catch (error) {
    ydoc.destroy();
    throw error;
  }
}

async function importYDocArchiveEntries(
  collection: Workspace,
  blobs: readonly [string, Blob][],
  options: ZipImportOptions = {}
): Promise<Store[]> {
  if (blobs.length === 0) return [];

  let entries: YDocArchiveEntry[] = [];
  reportZipImport(options, 'snapshot-preparing', 0, blobs.length);
  for (const [sourceDocId, blob] of blobs) {
    try {
      entries.push(await readYDocArchiveEntry(sourceDocId, blob));
    } catch (error) {
      entries.forEach(entry => entry.ydoc.destroy());
      throw error;
    }
    reportZipImport(
      options,
      'snapshot-preparing',
      entries.length,
      blobs.length
    );
    if (entries.length % 8 === 0) await importYield(options);
  }
  options.onYDocArchiveProgress?.({
    processedDocs: 0,
    totalDocs: entries.length,
  });
  const blockIdMap = collectBlockIds(collection, entries);
  entries = remapDatabaseDirectories(collection, entries, blockIdMap);
  const docIdMap = buildDocIdMap(collection, entries, blockIdMap);
  const imported: Store[] = [];

  const docReferenceColumns = collectDatabaseColumnLookup(
    entries,
    readDatabaseDocReferenceCellKeys
  );
  reportZipImport(options, 'snapshot-remapping', 0, entries.length);
  // Prune before remapping: legacy file-index blocks may only be identifiable
  // by their source block/database id. A page prunes with its parts.
  const archivedParts = new Map<string, Map<string, Y.Doc>>();
  for (const entry of entries) {
    const part = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (part?.kind !== 'page_part') continue;
    const ofPage = archivedParts.get(part.pageId) ?? new Map<string, Y.Doc>();
    ofPage.set(part.partId, entry.ydoc);
    archivedParts.set(part.pageId, ofPage);
  }
  for (const entry of entries)
    if (!isPagePartDocId(entry.sourceDocId))
      pruneImportedFileIndexSystemDatabases(
        entry.ydoc,
        archivedParts.get(entry.sourceDocId)
      );
  for (const [index, entry] of entries.entries()) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId) {
      throw new Error(`Archive doc "${entry.sourceDocId}" has no id mapping.`);
    }
    entry.ydoc.transact(() => {
      // Folder hierarchy is imported through the archive's explicit folder
      // metadata. A creation-time hint embedded in a source YDoc belongs to
      // the source workspace and must be removed before the YDoc can be
      // attached to the target sync engine.
      removeSourceWorkspacePlacement(entry.ydoc);
      const pagePart = readPageSubdocId(entry.sourceDocId)?.subdoc;
      remapYDocBlocks(
        collection,
        entry.ydoc,
        docIdMap,
        blockIdMap,
        pagePart?.kind === 'page_part' ? pagePart.partId : PAGE_DOCUMENT_PART_ID
      );
      const rowShard = readPageSubdocId(entry.sourceDocId)?.subdoc;
      const docReferences =
        rowShard?.kind === 'database-row-shard' && !rowShard.local
          ? docReferenceColumns.get(rowShard.databaseId)
          : undefined;
      if (docReferences?.size)
        remapDatabaseRowDocReferences(entry.ydoc, docReferences, docIdMap);
    });
    reportZipImport(options, 'snapshot-remapping', index + 1, entries.length);
    if ((index + 1) % 8 === 0) await importYield(options);
  }

  reportZipImport(options, 'snapshot-checking');
  await importYield(options);
  const databaseSystemDocAccessByAccessDocId =
    collectPreLoadDatabaseSystemDocAccessBindings(entries, docIdMap);
  const blockIdReplacements = Array.from(
    blockIdMap,
    ([sourceBlockId, targetBlockId]) => ({
      sourceBlockId,
      targetBlockId,
    })
  );

  await options.beforeYDocArchiveDocsLoaded?.(
    entries.map(entry => {
      const targetDocId = docIdMap.get(entry.sourceDocId);
      if (!targetDocId) {
        throw new Error(
          `Archive doc "${entry.sourceDocId}" has no id mapping.`
        );
      }
      return {
        sourceDocId: entry.sourceDocId,
        targetDocId,
        system: isSystemArchiveDocId(targetDocId),
        blockIdReplacements,
        databaseSystemDocAccess:
          databaseSystemDocAccessByAccessDocId.get(targetDocId) ?? [],
      };
    })
  );

  reportZipImport(options, 'snapshot-importing', 0, entries.length);
  await importYield(options);
  const databaseDocs = entries.flatMap(entry => {
    const docId = docIdMap.get(entry.sourceDocId);
    return docId && isSystemArchiveDocId(docId) && !isPagePartDocId(docId)
      ? [{ docId, update: Y.encodeStateAsUpdate(entry.ydoc) }]
      : [];
  });
  if (databaseDocs.length) {
    if (!options.publishDatabaseArchiveDocs) {
      throw new Error('Archive database docs need a database publisher.');
    }
    await options.publishDatabaseArchiveDocs(
      databaseDocs,
      collectArchiveDatabasePages(entries, docIdMap)
    );
  }
  for (const [index, entry] of entries.entries()) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId) {
      throw new Error(`Archive doc "${entry.sourceDocId}" has no id mapping.`);
    }

    if (isPagePartDocId(targetDocId)) {
      // A page part is a document of its page, written as the editor writes
      // its parts; it is no page of its own.
      const part = ensureWorkspaceDoc(collection, targetDocId);
      if (!part) {
        throw new Error(`Page part doc "${targetDocId}" could not be created.`);
      }
      part.load();
      Y.applyUpdate(part.spaceDoc, Y.encodeStateAsUpdate(entry.ydoc));
      options.onYDocArchiveProgress?.({
        processedDocs: index + 1,
        totalDocs: entries.length,
      });
      reportZipImport(options, 'snapshot-importing', index + 1, entries.length);
      continue;
    }
    const system = isSystemArchiveDocId(targetDocId);
    // Database docs were published above; the journal applied them already.
    const targetDoc = system
      ? collection.getDoc(targetDocId)
      : createArchiveDoc(collection, targetDocId, {
          transientPageDoc: options.transientPageDocs,
        });
    if (!targetDoc) {
      throw new Error(`Published archive doc "${targetDocId}" is missing.`);
    }
    targetDoc.load();
    if (!system) {
      Y.applyUpdate(targetDoc.spaceDoc, Y.encodeStateAsUpdate(entry.ydoc));
      if (!options.transientPageDocs) {
        restoreArchiveDocumentTitle(collection.meta, targetDocId, entry.ydoc);
      }
    }
    imported.push(targetDoc.getStore({ id: targetDocId }));
    options.onYDocArchiveProgress?.({
      processedDocs: index + 1,
      totalDocs: entries.length,
    });
    reportZipImport(options, 'snapshot-importing', index + 1, entries.length);
    if ((index + 1) % 8 === 0) await importYield(options);
  }

  return imported;
}

function yDocUpdateBlob(update: Uint8Array): Blob {
  return new Blob([
    update.buffer.slice(
      update.byteOffset,
      update.byteOffset + update.byteLength
    ) as ArrayBuffer,
  ]);
}

function cloneStoreYDocForArchive(store: Store): Y.Doc {
  const ydoc = new Y.Doc({ guid: store.id });
  Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(store.spaceDoc));
  writeArchiveDocumentTitle(ydoc, store.meta?.title);
  return ydoc;
}

function isFileIndexSystemDatabaseBlock(block: Y.Map<unknown>): boolean {
  const databaseId = block.get('prop:databaseId');
  const blockId = block.get('sys:id');
  const fileIndexId =
    typeof databaseId === 'string'
      ? databaseId
      : typeof blockId === 'string'
        ? blockId
        : undefined;
  return (
    block.get('sys:flavour') === 'affine:database' &&
    block.get('prop:visible') !== true &&
    (block.get('prop:managedBy') === FILE_INDEX_MANAGED_BY ||
      isFileIndexDatabaseId(fileIndexId))
  );
}

function isDocScopedSystemDatabaseBlock(block: {
  id?: string;
  props: {
    databaseId?: unknown;
    docScoped?: unknown;
    managedBy?: unknown;
    systemManaged?: unknown;
    visible?: unknown;
  };
}): boolean {
  const databaseId =
    typeof block.props.databaseId === 'string'
      ? block.props.databaseId
      : block.id;
  return (
    isHiddenSystemManagedDatabase({
      id: databaseId,
      managedBy: block.props.managedBy,
      visible: block.props.visible,
    }) ||
    (block.props.systemManaged === true && block.props.docScoped === true)
  );
}

/** Purges hidden File Index databases of a page and of its part documents. */
export function pruneImportedFileIndexSystemDatabases(
  ydoc: Y.Doc,
  parts: ReadonlyMap<string, Y.Doc> = new Map()
): void {
  const roots: string[] = [];
  for (const doc of [ydoc, ...parts.values()])
    pagePartBlocksMap(doc).forEach((block, blockId) => {
      if (isYMap(block) && isFileIndexSystemDatabaseBlock(block))
        roots.push(blockId);
    });
  if (roots.length > 0) purgePageSubtrees(ydoc, roots, parts);
}

function getStoreForExistingDoc(
  collection: Workspace,
  docId: string,
  owner: string
): Store {
  const doc = collection.getDoc(docId);
  if (!doc) {
    throw new Error(
      `Cannot export sharded database "${owner}" because document "${docId}" is not available.`
    );
  }
  if (!doc.ready) {
    doc.load();
  }
  return doc.getStore({ id: docId });
}

function addStore(stores: Store[], seen: Set<string>, store: Store): void {
  if (seen.has(store.id)) return;
  seen.add(store.id);
  stores.push(store);
}

function walkStoreBlocks(
  store: Store,
  visit: (block: NonNullable<Store['root']>) => void
): void {
  const walk = (block: NonNullable<Store['root']>): void => {
    visit(block);
    block.children.forEach(child => walk(child));
  };

  if (store.root) {
    walk(store.root);
  }
}

function collectReferencedDatabaseRootDocIds(store: Store): string[] {
  const rootDocIds = new Set<string>();

  walkStoreBlocks(store, block => {
    if (block.flavour !== 'affine:database') return;
    if (isDocScopedSystemDatabaseBlock(block)) {
      return;
    }

    const rootDocId = (block.props as { databaseRootDocId?: unknown })
      .databaseRootDocId;
    if (typeof rootDocId === 'string' && rootDocId) {
      rootDocIds.add(rootDocId);
    }
  });

  return [...rootDocIds];
}

/** Database blocks of a store whose documents an export carries. */
function walkExportedDatabaseBlocks(
  store: Store,
  visit: (block: NonNullable<Store['root']>) => void
): void {
  walkStoreBlocks(store, block => {
    if (block.flavour !== 'affine:database') return;
    if (isDocScopedSystemDatabaseBlock(block)) return;
    visit(block);
  });
}

function collectDatabaseShardManifests(store: Store): DatabaseShardManifest[] {
  const manifests: DatabaseShardManifest[] = [];

  walkExportedDatabaseBlocks(store, block => {
    const manifest = readDatabaseShardDirectory(block);
    if (manifest) {
      manifests.push(manifest);
    }
  });

  return manifests;
}

/** Directory part documents of the exported databases of a store. */
function collectDatabaseDirectoryPartDocIds(
  store: Store
): { databaseBlockId: string; partDocId: string }[] {
  const parts: { databaseBlockId: string; partDocId: string }[] = [];

  walkExportedDatabaseBlocks(store, block => {
    for (const partDocId of readDatabaseShardDirectoryDocIds(block)
      ?.partDocIds ?? []) {
      parts.push({ databaseBlockId: block.id, partDocId });
    }
  });

  return parts;
}

function collectYDocArchiveStores(
  collection: Workspace,
  docs: readonly Store[]
): Store[] {
  const stores: Store[] = [];
  const seen = new Set<string>();

  docs.forEach(doc => addStore(stores, seen, doc));

  let index = 0;
  while (index < stores.length) {
    const store = stores[index]!;
    index++;

    for (const rootDocId of collectReferencedDatabaseRootDocIds(store)) {
      addStore(
        stores,
        seen,
        getStoreForExistingDoc(collection, rootDocId, store.id)
      );
    }

    // The directory parts first: the shard list is read from them.
    for (const {
      databaseBlockId,
      partDocId,
    } of collectDatabaseDirectoryPartDocIds(store)) {
      addStore(
        stores,
        seen,
        getStoreForExistingDoc(collection, partDocId, databaseBlockId)
      );
    }

    for (const manifest of collectDatabaseShardManifests(store)) {
      for (const shard of manifest.shards) {
        const existingDocId = collection.getDoc(shard.serverDocId)
          ? shard.serverDocId
          : null;
        if (!existingDocId) {
          throw new Error(
            `Cannot export sharded database "${manifest.databaseId}" because row shard "${shard.shardId}" is not available.`
          );
        }
        addStore(
          stores,
          seen,
          getStoreForExistingDoc(collection, existingDocId, manifest.databaseId)
        );
      }
    }
  }

  return stores;
}

function readYValue(value: unknown): unknown {
  if (isYMap(value)) {
    const record: Record<string, unknown> = {};
    value.forEach((item, key) => {
      record[key] = readYValue(item);
    });
    return record;
  }

  if (isYArray(value)) {
    return value.toArray().map(readYValue);
  }

  if (value instanceof Y.Text) {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map(readYValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        readYValue(item),
      ])
    );
  }

  return value;
}

/** What `read` finds in each database block of the archive, by source block
 * id and by source database id. */
function collectDatabaseColumnLookup<T>(
  entries: readonly YDocArchiveEntry[],
  read: (block: Y.Map<unknown>) => T
): Map<string, T> {
  const lookup = new Map<string, T>();

  entries.forEach(entry => {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    blocks.forEach((block, blockId) => {
      if (!isYMap(block) || block.get('sys:flavour') !== 'affine:database') {
        return;
      }

      const columns = read(block);
      const databaseId = block.get('prop:databaseId');
      lookup.set(blockId, columns);
      if (typeof databaseId === 'string' && databaseId) {
        lookup.set(databaseId, columns);
      }
    });
  });

  return lookup;
}

function collectBlobColumnLookup(
  entries: readonly YDocArchiveEntry[]
): BlobColumnLookup {
  return collectDatabaseColumnLookup(entries, databaseBlockBlobCellKeys);
}

/** Keys of the cells naming another doc of the workspace. */
function readDatabaseDocReferenceCellKeys(
  block: Y.Map<unknown>
): ReadonlySet<string> {
  const columns = readYValue(block.get('prop:columns'));
  return new Set(
    databaseCellKeysOfType(
      Array.isArray(columns) ? columns : [],
      'doc-reference'
    )
  );
}

/** A doc-reference cell of a row shard follows its doc to the target id; a
 * doc outside the archive keeps its id, like any other doc reference. */
function remapDatabaseRowDocReferences(
  ydoc: Y.Doc,
  columnIds: ReadonlySet<string>,
  docIdMap: ReadonlyMap<string, string>
) {
  ydoc.getMap<unknown>(DATABASE_ROW_SHARD_ROWS_MAP_KEY).forEach(row => {
    if (!isYMap(row)) return;
    for (const columnId of columnIds) {
      const cell = row.get(columnId);
      if (isYMap(cell)) {
        remapStringField(cell, 'value', docIdMap);
        continue;
      }
      if (!cell || typeof cell !== 'object' || Array.isArray(cell)) continue;
      const value = (cell as { value?: unknown }).value;
      const mapped = typeof value === 'string' && docIdMap.get(value);
      if (mapped) row.set(columnId, { ...cell, value: mapped });
    }
  });
}

function collectYDocArchiveBlobIds(
  entries: readonly YDocArchiveEntry[],
  inheritedBlobColumnLookup?: Map<string, DatabaseBlobCellKeys>
): Set<string> {
  const blobIds = new Set<string>();
  const blobColumnLookup =
    inheritedBlobColumnLookup ?? new Map<string, DatabaseBlobCellKeys>();
  for (const [key, columns] of collectBlobColumnLookup(entries)) {
    blobColumnLookup.set(key, columns);
  }

  const partsByPage = new Map<string, Map<string, Y.Doc>>();
  for (const entry of entries) {
    const part = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (part?.kind !== 'page_part') continue;
    let parts = partsByPage.get(part.pageId);
    if (!parts) partsByPage.set(part.pageId, (parts = new Map()));
    parts.set(part.partId, entry.ydoc);
  }
  const placedByPage = new Map<string, Set<string>>();
  for (const entry of entries) {
    if (isSystemArchiveDocId(entry.sourceDocId)) continue;
    const parts = partsByPage.get(entry.sourceDocId) ?? new Map();
    assertPagePartsLoaded(entry.ydoc, parts.keys());
    const page = readPage(pageReadSource(entry.ydoc, parts));
    placedByPage.set(entry.sourceDocId, new Set(pagePlacedBlockIds(page.tree)));
  }

  entries.forEach(entry => {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    // Archives can originate in a different workspace; remapping must read
    // the source identity rather than require the destination workspace.
    const root = readPageSubdocId(entry.sourceDocId)?.subdoc;
    let ids: string[];
    if (root?.kind === 'database-root') {
      ids = [root.databaseId];
    } else if (root?.kind === 'page_part') {
      const placed = placedByPage.get(root.pageId);
      if (!placed)
        throw new Error('Page part has no archive page: ' + root.pageId);
      ids = [
        ...readPageStructureEntries(
          pageStructureMap(entry.ydoc),
          root.partId
        ).keys(),
      ].filter(id => placed.has(id));
    } else {
      ids = [...(placedByPage.get(entry.sourceDocId) ?? [])];
    }
    ids.forEach(id => {
      const block = blocks.get(id);
      if (!isYMap(block)) return;

      addBlobId(blobIds, block.get('prop:sourceId'));

      if (block.get('sys:flavour') !== 'affine:database') return;

      const columns = databaseBlockBlobCellKeys(block);
      collectDatabaseCellBlobIds(
        blobIds,
        readYValue(block.get(DATABASE_INLINE_CELLS_PROP)),
        columns
      );
    });

    const rowShard = readPageSubdocId(entry.sourceDocId)?.subdoc;
    if (rowShard?.kind !== 'database-row-shard' || rowShard.local) return;

    const columns = blobColumnLookup.get(rowShard.databaseId);
    if (!columns) return;

    collectDatabaseCellBlobIds(
      blobIds,
      readYValue(entry.ydoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)),
      columns
    );
  });

  return blobIds;
}

function getAssetExtension(blob: Blob): string {
  for (const [ext, mime] of extMimeMap) {
    if (mime === blob.type) return ext;
  }
  return 'bin';
}

async function writeYDocArchiveAssets(
  zip: Zip,
  collection: Workspace,
  blobIds: ReadonlySet<string>,
  excludeBlobIds?: ReadonlySet<string>
): Promise<void> {
  const assets = zip.folder('assets');
  await Promise.all(
    [...blobIds]
      .filter(blobId => !excludeBlobIds?.has(blobId))
      .map(async blobId => {
        const blob = await collection.blobSync.get(blobId);
        if (!blob) {
          throw new Error(
            `Cannot export referenced blob "${blobId}" because it is not available.`
          );
        }
        await assets.file(`${blobId}.${getAssetExtension(blob)}`, blob);
      })
  );
}

async function exportYDocArchive(
  collection: Workspace,
  docs: readonly Store[],
  excludeBlobIds?: Set<string>
) {
  const stores = collectYDocArchiveStores(collection, docs);
  const entries = stores.flatMap(store =>
    pageArchiveEntries(collection, store)
  );
  const zip = new Zip();

  await Promise.all(
    entries.map(entry =>
      zip.file(
        `docs/${entry.sourceDocId}.ydoc`,
        yDocUpdateBlob(Y.encodeStateAsUpdate(entry.ydoc))
      )
    )
  );

  await writeYDocArchiveAssets(
    zip,
    collection,
    collectYDocArchiveBlobIds(entries),
    excludeBlobIds
  );

  const downloadBlob = await zip.generate();
  return download(downloadBlob, `${collection.id}.bs.zip`);
}

export type WorkspaceArchiveProgress =
  | { phase: 'documents'; processedDocs: number }
  | { phase: 'assets'; processedAssets: number }
  | { phase: 'finalizing' };

/**
 * Incremental workspace archive writer. Callers retain only one loaded document
 * family at a time; compressed output is drained to the supplied sink before
 * the next family is accepted.
 */
export class WorkspaceYDocArchiveWriter {
  private readonly familyBlobColumnLookup = new Map<
    string,
    DatabaseBlobCellKeys
  >();
  private readonly zip: StreamingZip;
  private processedDocs = 0;
  private processedAssets = 0;

  constructor(
    private readonly collection: Workspace,
    sink: (chunk: Uint8Array) => Promise<void>,
    private readonly options: {
      excludeBlobIds?: ReadonlySet<string>;
      claimBlobId?: (id: string) => Promise<boolean>;
      claimDocId?: (id: string) => Promise<boolean>;
      signal?: AbortSignal;
      onProgress?: (progress: WorkspaceArchiveProgress) => void;
    } = {}
  ) {
    this.zip = new StreamingZip(sink);
  }

  async appendFamily(stores: readonly Store[]): Promise<void> {
    this.throwIfAborted();
    for (const store of stores) {
      if (
        this.options.claimDocId &&
        !(await this.options.claimDocId(store.id))
      ) {
        continue;
      }
      const entries = pageArchiveEntries(this.collection, store);
      try {
        for (const entry of entries) {
          this.throwIfAborted();
          await this.zip.file(
            `docs/${entry.sourceDocId}.ydoc`,
            yDocUpdateBlob(Y.encodeStateAsUpdate(entry.ydoc))
          );
        }
        this.processedDocs += 1;
        this.options.onProgress?.({
          phase: 'documents',
          processedDocs: this.processedDocs,
        });
        for (const blobId of collectYDocArchiveBlobIds(
          entries,
          this.familyBlobColumnLookup
        )) {
          if (this.options.excludeBlobIds?.has(blobId)) continue;
          if (
            this.options.claimBlobId &&
            !(await this.options.claimBlobId(blobId))
          ) {
            continue;
          }
          this.throwIfAborted();
          const blob = await this.collection.blobSync.get(blobId);
          if (!blob) {
            throw new Error(
              `Cannot export referenced blob "${blobId}" because it is not available.`
            );
          }
          await this.zip.file(
            `assets/${blobId}.${getAssetExtension(blob)}`,
            blob
          );
          this.processedAssets += 1;
          this.options.onProgress?.({
            phase: 'assets',
            processedAssets: this.processedAssets,
          });
        }
      } finally {
        for (const entry of entries) entry.ydoc.destroy();
      }
    }
  }

  finishFamily(): void {
    this.familyBlobColumnLookup.clear();
  }

  async close(): Promise<void> {
    this.options.onProgress?.({ phase: 'finalizing' });
    await this.zip.generate();
  }

  private throwIfAborted(): void {
    this.options.signal?.throwIfAborted();
  }
}

async function exportDocs(
  collection: Workspace,
  schema: Schema,
  docs: Store[],
  excludeBlobIds?: Set<string>
) {
  if (docs.length === 0) {
    throw new Error('Snapshot export requires at least one document.');
  }

  if (requiresYDocArchiveExport(docs)) {
    return exportYDocArchive(collection, docs, excludeBlobIds);
  }

  const zip = new Zip();
  const job = new Transformer({
    schema,
    blobCRUD: collection.blobSync,
    docCRUD: {
      create: (id: string) => collection.createDoc(id).getStore({ id }),
      get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
      delete: (id: string) => collection.removeDoc(id),
    },
    middlewares: [
      replaceIdMiddleware(collection.idGenerator),
      titleMiddleware(collection.meta.docMetas),
    ],
  });
  const snapshotResults = await Promise.all(
    docs.map(async doc => ({
      doc,
      snapshot: job.docToSnapshot(doc),
    }))
  );
  const failedDocs = snapshotResults.filter(result => !result.snapshot);
  if (failedDocs.length > 0) {
    const labels = failedDocs.map(({ doc }) => doc.meta?.title || doc.id);
    throw new Error(
      `Snapshot export failed for document(s): ${labels.join(', ')}`
    );
  }
  const snapshots = snapshotResults.map(result => result.snapshot!);

  await Promise.all(
    snapshots.map(async snapshot => {
      // Use the title and id as the snapshot file name
      const title = snapshot.meta.title || 'untitled';
      const id = snapshot.meta.id;
      const snapshotName = `${title}-${id}.snapshot.json`;
      await zip.file(snapshotName, JSON.stringify(snapshot, null, 2));
    })
  );

  const assets = zip.folder('assets');
  const pathBlobIdMap = job.assetsManager.getPathBlobIdMap();
  const assetsMap = job.assets;

  // Add blobs to assets folder, if failed, log the error and continue
  // sl-ht07.5: Filter out excluded blobs (user deselected in attachment dialog)
  const blobIdsToExport = excludeBlobIds?.size
    ? Array.from(pathBlobIdMap.values()).filter(id => !excludeBlobIds.has(id))
    : Array.from(pathBlobIdMap.values());

  const results = await Promise.all(
    blobIdsToExport.map(async blobId => {
      try {
        await job.assetsManager.readFromBlob(blobId);
        const ext = getAssetName(assetsMap, blobId).split('.').at(-1);
        const blob = assetsMap.get(blobId);
        if (blob) {
          await assets.file(`${blobId}.${ext}`, blob);
          return { success: true, blobId };
        }
        return { success: false, blobId, error: 'Blob not found' };
      } catch (error) {
        console.error(`Failed to process blob: ${blobId}`, error);
        return { success: false, blobId, error };
      }
    })
  );

  const failures = results.filter(r => !r.success);
  if (failures.length > 0) {
    console.warn(`Failed to process ${failures.length} blobs:`, failures);
  }

  const downloadBlob = await zip.generate();
  // Use the collection id as the zip file name
  return download(downloadBlob, `${collection.id}.bs.zip`);
}

function requiresYDocArchiveExport(docs: readonly Store[]): boolean {
  return docs.some(
    doc =>
      collectReferencedDatabaseRootDocIds(doc).length > 0 ||
      collectDatabaseShardManifests(doc).length > 0
  );
}

async function writeYDocArchiveAssetBlobs(
  collection: Workspace,
  assetBlobs: readonly [string, Blob][],
  options: ZipImportOptions
): Promise<void> {
  let processed = 0;
  reportZipImport(options, 'snapshot-assets', 0, assetBlobs.length);
  for (let offset = 0; offset < assetBlobs.length; offset += 4) {
    const results = await Promise.allSettled(
      assetBlobs.slice(offset, offset + 4).map(async ([name, blob]) => {
        const nameWithExt = name.replace('assets/', '');
        const assetId = nameWithExt.replace(/\.[^/.]+$/, '');
        const ext = nameWithExt.split('.').at(-1) ?? '';
        const mime = extMimeMap.get(ext) ?? blob.type;
        const file = new File([blob], nameWithExt, { type: mime });
        await collection.blobSync.set(assetId, file);
        reportZipImport(
          options,
          'snapshot-assets',
          ++processed,
          assetBlobs.length
        );
      })
    );
    for (const result of results) {
      if (result.status === 'rejected') throw result.reason;
    }
    await importYield(options);
  }
}

async function importDocs(
  collection: Workspace,
  schema: Schema,
  imported: Blob,
  options: ZipImportOptions = {}
) {
  reportZipImport(options, 'snapshot-reading');
  await importYield(options);
  const unzip = new Unzip();
  // fflate's async API delegates large entries to workers instead of blocking
  // the main thread for the entire archive inflation.
  await unzip.load(imported, { asynchronous: true });

  const assetBlobs: [string, Blob][] = [];
  const snapshotsBlobs: Blob[] = [];
  const ydocBlobs: [string, Blob][] = [];

  for (const { path, content: blob } of unzip) {
    if (path.includes('MACOSX') || path.includes('DS_Store')) {
      continue;
    }

    if (isArchiveDirectory(path)) {
      continue;
    }

    if (path.startsWith('assets/')) {
      assetBlobs.push([path, blob]);
      continue;
    }

    if (path === 'info.json') {
      continue;
    }

    if (path.endsWith('.snapshot.json')) {
      snapshotsBlobs.push(blob);
      continue;
    }

    const archiveDocId = readArchiveDocId(path);
    if (archiveDocId) {
      ydocBlobs.push([archiveDocId, blob]);
      continue;
    }
  }

  if (snapshotsBlobs.length > 0 && ydocBlobs.length > 0) {
    throw new Error(
      'ZIP import cannot mix snapshot JSON docs and docs/*.ydoc archive docs.'
    );
  }

  // Once the archive is inventoried, report its actual import steps. The UI
  // can track whole-process completion without guessing time-based weights.
  const phases: ZipImportProgress['phase'][] =
    ydocBlobs.length > 0
      ? [
          'snapshot-reading',
          ...(assetBlobs.length > 0 ? ['snapshot-assets' as const] : []),
          'snapshot-preparing',
          'snapshot-remapping',
          'snapshot-checking',
          'snapshot-importing',
        ]
      : ['snapshot-reading', 'snapshot-importing'];
  const onProgress = options.onProgress;
  options = {
    ...options,
    onProgress: onProgress
      ? progress => onProgress({ ...progress, phases })
      : undefined,
  };
  reportZipImport(options, 'snapshot-reading', 1, 1);

  if (ydocBlobs.length > 0) {
    if (assetBlobs.length > 0) {
      await writeYDocArchiveAssetBlobs(collection, assetBlobs, options);
    }
    // YDoc archives remap references before live attachment. They need neither
    // the JSON Transformer/middleware subscriptions nor a duplicate asset map.
    return importYDocArchiveEntries(collection, ydocBlobs, options);
  }

  // sl-ht07.3: Track old→new ID mappings for StreamLain-specific props
  const docIdMap = new Map<string, string>();
  const blockIdMap = new Map<string, string>();

  const job = new Transformer({
    schema,
    blobCRUD: collection.blobSync,
    docCRUD: {
      create: (id: string) => collection.createDoc(id).getStore({ id }),
      get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
      delete: (id: string) => collection.removeDoc(id),
    },
    middlewares: [
      // ID tracker MUST be first to capture original IDs before replaceIdMiddleware mutates them
      createStreamlainIdTracker(docIdMap, blockIdMap),
      replaceIdMiddleware(collection.idGenerator),
      titleMiddleware(collection.meta.docMetas),
    ],
  });
  const assetsMap = job.assets;

  assetBlobs.forEach(([name, blob]) => {
    const nameWithExt = name.replace('assets/', '');
    const assetsId = nameWithExt.replace(/\.[^/.]+$/, '');
    const ext = nameWithExt.split('.').at(-1) ?? '';
    const mime = extMimeMap.get(ext) ?? '';
    const file = new File([blob], nameWithExt, {
      type: mime,
    });
    assetsMap.set(assetsId, file);
  });

  // A shared attachment may be referenced by many blocks/documents. Read and
  // hash its bytes once for this import; each reference still gets rewritten.
  const assetHashes = new Map<string, Promise<string>>();
  const snapshotStores: Store[] = [];
  if (snapshotsBlobs.length > 0) {
    reportZipImport(options, 'snapshot-importing', 0, snapshotsBlobs.length);
    await importYield(options);
  }
  for (const blob of snapshotsBlobs) {
    const json = await blob.text();
    const snapshot = JSON.parse(json) as DocSnapshot;
    upgradeLegacyDocSnapshot({
      docId: snapshot.meta.id,
      snapshot,
      source: 'zip-snapshot-import',
      sourceDocId: snapshot.meta.id,
    });
    const tasks: Promise<void>[] = [];

    job.walk(snapshot, block => {
      const sourceId = block.props?.sourceId as string | undefined;
      if (!sourceId?.startsWith('/')) return;
      const removeSlashId = sourceId.replace(/^\//, '');
      const asset = assetsMap.get(removeSlashId);
      if (!asset) return;
      let hash = assetHashes.get(removeSlashId);
      if (!hash) {
        hash = asset.arrayBuffer().then(buffer => sha(buffer));
        assetHashes.set(removeSlashId, hash);
      }
      tasks.push(
        hash.then(id => {
          assetsMap.set(id, asset);
          block.props.sourceId = id;
        })
      );
    });

    await Promise.all(tasks);
    const store = await job.snapshotToDoc(snapshot);
    if (!store) {
      throw new Error(
        `Could not import snapshot document "${snapshot.meta.title || snapshot.meta.id}".`
      );
    }
    snapshotStores.push(store);
    reportZipImport(
      options,
      'snapshot-importing',
      snapshotStores.length,
      snapshotsBlobs.length
    );
    await importYield(options);
  }
  // sl-ht07.3: Post-import fixup — remap docId/blockId references in
  // Automation, Ghost, and Database (relation column) blocks.
  remapStreamlainReferences(snapshotStores, docIdMap, blockIdMap);

  return snapshotStores;
}

export const ZipTransformer = {
  exportDocs,
  importDocs,
};
